export const aiAgent = {
  head: {
    chip: "AI Reception",
    listingLabel: "Listing:",
  },
  globalNote: {
    title: "Account-wide settings",
    body: "Applies to <b>ALL your properties</b>. The AI Agent attends to anyone writing for the first time to your <b>connected WhatsApp number</b> who is <b>not yet a customer and has no booking</b>: it answers their questions and guides them toward booking.",
  },
  perListingNote: {
    title: "Configured per listing",
    body: "Invoice issuing uses the <b>billing issuer</b> linked to each listing, so it's enabled and adjusted in each listing's settings (not globally). Open a listing and go to «Invoice issuing».",
  },
  noEmisor: {
    title: "This listing has no billing issuer",
    body: "Invoice issuing can't be enabled until you link an issuer to this listing in <b>My account → Electronic invoicing</b>. Each issuer is assigned to one listing.",
  },
  master: {
    requiresEmisor: "Requires a billing issuer",
    on: "On",
    off: "Off",
  },
  cartelQr: {
    title: "Check-in poster for reception",
    hint: "Print it and leave it on the desk: the guest scans it, types their booking number and checks in from their phone. Handy when the OTA doesn't send every detail.",
    steps: "The guest scans · types their booking number · completes their details.",
    download: "Download poster (PDF)",
    downloading: "Preparing…",
    downloadError: "The poster could not be prepared.",
    copy: "Copy link",
    copied: "Link copied",
    language: "Poster language",
    alsoEnglish: "Add English underneath",

    copyManual: "Could not copy. Select the link and copy it by hand.",
    popupBlocked: "The browser blocked the window. Copy the link and open it in a new tab.",
    loadError: "The poster could not be loaded.",
    retry: "Retry",  },
  checkinSchedule: {
    title: "When does check-in start?",
    hint: "Schedule when the agent sends the check-in link to the guest. Applies at listing level.",
  },
  avisoCheckin: {
    title: "Email a copy when the check-in is completed",
    hint: "When the group finishes registering, we send the booking summary — lead guest, companions and their IDs — with the signed form attached. Handy for the front desk or security.",
    enable: "Send the notice",
    steps: "Sent once, when the last person in the group registers.",
    placeholder: "frontdesk@yourproperty.com",
    add: "Add email",
    remove: "Remove",
    max: "Up to {n} emails.",
    save: "Save",
    saving: "Saving…",
    saved: "Notice saved",
    saveError: "Could not save.",
    badEmail: "“{email}” is not a valid address.",
    needOne: "Add at least one email to turn the notice on.",
  },
  field: {
    optional: "(optional)",
    yes: "Yes",
    no: "No",
  },
  variant: {
    nationality: "Nationality",
    writingStyle: "Writing style",
  },
  robot: {
    title: "What the agent knows",
  },
  pdf: {
    loaded: "Document uploaded · the agent will read its content",
    remove: "Remove",
    replace: "Replace PDF",
    dropTitle: "Upload a PDF or Word file with the property guide",
    dropHint: "PDF or .docx · max 15 MB",
  },
  payments: {
    required: "Requires connecting a payment platform",
    connect: "Connect a payment platform →",
  },
  faq: {
    qPlaceholder: "Question · e.g. Is there parking?",
    aPlaceholder: "Answer · e.g. Yes, free at the door.",
  },
  list: {
    remove: "Remove",
    add: "Add",
  },
  footer: {
    saved: "Saved",
    save: "Save changes",
  },
  wa: {
    online: "online",
    caption: "Preview · updates with your settings",
    defaultName: "AI Reception",
  },
  errors: {
    pdfType: "The file must be a PDF or a Word (.docx).",
    pdfSize: "The file exceeds 15 MB.",
    pdfNoText: "We couldn't read any text from the document (is it a scanned or empty PDF?). Try another one or use free text.",
    pdfUpload: "The PDF could not be uploaded.",
    pdfRemove: "The PDF could not be removed.",
    loadConfig: "The settings could not be loaded.",
    saveConfig: "Could not save. Please try again.",
  },
  features: {
    general: {
      title: "General settings",
      subtitle: "Define the agent's identity and style. This is an ACCOUNT setting: it applies to ALL your properties.",
      sections: {
        identity: {
          label: "Agent identity",
          hint: "How it introduces itself and what style it replies in.",
        },
      },
      fields: {
        agentName: {
          label: "Agent name",
          placeholder: "e.g. Sofía, Loft Ámbar Reception…",
        },
        persona: {
          label: "Personality",
          options: {
            amigable: "Friendly and warm",
            profesional: "Professional and resourceful",
            eficiente: "Direct and efficient",
            entusiasta: "Enthusiastic and warm",
            sereno: "Calm and relaxed",
          },
        },
        tone: {
          label: "Reply tone",
          options: {
            cercano: "Casual",
            cordial: "Cordial",
            formal: "Formal",
            profesional: "Professional",
          },
        },
        responseLength: {
          label: "Reply length",
          options: {
            brief: "Brief (1-2 sentences)",
            balanced: "Balanced",
            detailed: "Detailed",
          },
        },
        useEmojis: {
          label: "Use emojis",
        },
        language: {
          label: "Main language",
          options: {
            auto: "Automatic (based on guest)",
            es: "Spanish",
            en: "English",
            fr: "French",
            de: "German",
            pt: "Portuguese",
            it: "Italian",
          },
        },
        spanishVariant: {
          label: "Writing style (Spanish)",
        },
        signature: {
          label: "Closing signature",
          placeholder: "e.g. — The Loft Ámbar team 🏡",
        },
      },
    },
    knowledge: {
      title: "Knowledge base",
      subtitle: "Everything the AI Agent needs to know to serve your guests accurately.",
      enableLabel: "The agent uses this knowledge base",
      sections: {
        knowledge: {
          label: "Property knowledge",
          hint: "Choose how you provide the information the agent will use.",
        },
        behavior: {
          label: "Behavior and limits",
          hint: "Set the agent's rules and when to ask for human help.",
        },
      },
      fields: {
        kbMode: {
          label: "How do you want to provide the information?",
          options: {
            form: "Forms",
            text: "Free text",
            pdf: "Upload a document",
          },
        },
        propertyType: {
          label: "Property type",
          options: {
            apartment: "Apartment",
            house: "House",
            villa: "Villa",
            loft: "Loft",
            room: "Room",
            cabin: "Cabin",
            hotel: "Hotel",
            hostel: "Hostel",
          },
        },
        kbText: {
          label: "Property information",
          placeholder: "Write or paste here EVERYTHING the agent should know: WiFi and password, parking, appliances, heating/AC, house rules, local recommendations…",
          hint: "The agent will use this text as-is to reply.",
        },
        kbPdf: {
          label: "Property document",
          hint: "Upload the property guide as a PDF or Word (.docx). The agent will read its content to reply.",
        },
        description: {
          label: "General information",
          placeholder: "How the heating and AC work, appliances, climate control, quirks of the property…",
          hint: "Main context the agent will use in every reply.",
        },
        wifiName: {
          label: "WiFi name",
          placeholder: "MyWifi_5G",
        },
        wifiPassword: {
          label: "WiFi password",
        },
        parkingInfo: {
          label: "Parking",
          placeholder: "Free parking at the door / Paid parking 100m away / No parking…",
        },
        amenities: {
          label: "Amenities and equipment",
          placeholder: "Equipped kitchen, washer, dryer, air conditioning, elevator, crib, towels…",
        },
        houseRules: {
          label: "House rules",
          placeholder: "No smoking, no parties, no pets, quiet hours 22:00–08:00…",
        },
        localRecommendations: {
          label: "Local recommendations",
          placeholder: "Restaurants, supermarkets, transport, what to visit nearby…",
        },
        faqs: {
          label: "Frequently asked questions",
          hint: "Question-and-answer pairs. You can attach images or videos to each one.",
        },
        links: {
          label: "Links and documents",
          itemPlaceholder: "https://… (house guide, manual, map)",
          hint: "External resources the agent can consult.",
        },
        sendAmenityPhotos: {
          label: "Send equipment and amenity photos",
          hint: "If a guest asks about an amenity that has photos, the agent answers and sends them. If it has none, it answers with text only.",
        },
        customInstructions: {
          label: "Custom instructions",
          placeholder: "Specific tone or content rules. E.g. always offer late checkout if they ask about departure.",
        },
        forbiddenTopics: {
          label: "Forbidden topics or actions",
          placeholder: "E.g. don't give discounts, don't confirm date changes without approval, don't share other guests' details.",
        },
        escalationRules: {
          label: "When to hand over to a human",
          placeholder: "E.g. if there's a breakdown, a serious complaint or a refund request, hand over to the host.",
        },
        fallbackMessage: {
          label: "Message when it doesn't know the answer",
          placeholder: "I don't have that detail to hand, but I'll let the host know and we'll get back to you right away 🙌",
        },
        humanContact: {
          label: "Host contact",
          placeholder: "+34 600 000 000",
        },
        supportHours: {
          label: "Human support hours",
          placeholder: "Mon–Sun 09:00–21:00",
        },
      },
    },
    checkin: {
      title: "Check-in",
      enableLabel: "Manage check-in automatically",
    },
    newBookings: {
      title: "New bookings and leads",
      subtitle: "The agent attends to new leads (people writing for the first time who want to book or have questions) and sends them the Direct Booking link so they complete the booking on the web. This is an ACCOUNT setting: it applies to all your properties.",
      enableLabel: "Attend to leads and booking requests",
      sections: {
        businessIdentity: {
          label: "Business identity",
          hint: "The name the agent introduces itself with to those who haven't booked yet.",
        },
        newLeads: {
          label: "New leads · first contact",
          hint: "How it attends to someone writing for the first time who is interested and hasn't booked yet.",
        },
        bookingLink: {
          label: "Booking link",
        },
      },
      fields: {
        businessName: {
          label: "Business name",
          placeholder: "e.g. Hostel Elios, Apartamentos del Puerto, Grupo Ámbar…",
          hint: "When someone without a booking asks who you are, the agent replies «I'm the assistant for [this name]». If you leave it empty it introduces itself generically, without saying which business it's from.",
        },
        leadWelcomeMessage: {
          label: "Welcome message for a new lead",
          placeholder: "Hi! 👋 I'm the property's assistant. Are you looking for dates for a getaway or do you have any questions? Happy to help 😊",
          hint: "First-contact greeting when someone writes for the first time. It isn't repeated in later messages.",
        },
        leadWelcomeVerbatim: {
          label: "Send the greeting as-is (without adapting it)",
          hint: "On: the agent uses your message word for word on first contact. Off: it adapts it to the tone of the conversation.",
        },
        answerKbForLeads: {
          label: "Answer questions even before they've booked",
          hint: "The agent answers their general questions with the knowledge base —even if they're not a guest yet— and can also send them the booking link.",
        },
        proactiveOffer: {
          label: "Proactively suggest booking",
          hint: "Even if the lead asks about other things, the agent encourages them to book and sends the link when appropriate, without pushing.",
        },
        offerMessage: {
          label: "Message when offering to book",
          placeholder: "Of course! You can book directly here 👇 I'll send you the link to pick your dates.",
          hint: "The tone the agent uses when offering the booking. The agent adds the link automatically.",
        },
      },
    },
    earlyAccess: {
      title: "Arrival time management",
      subtitle: "The agent manages early check-in requests: it records the arrival time the guest asks for and handles it according to your rules.",
      enableLabel: "Manage arrival times",
      sections: {
        request: {
          label: "Request handling",
          hint: "How the agent replies based on the requested time. If you turn these off, the request stays pending for you to choose (Approve / Approve another time / Prioritize / Reject).",
        },
      },
      fields: {
        autoTentativeEarly: {
          label: "Reply «subject to availability» if it's before check-in",
          hint: "The agent tells the guest you'll try; it stays pending your confirmation.",
        },
        autoApproveLate: {
          label: "Approve automatically if it's after check-in",
          hint: "An arrival within normal hours is confirmed on its own.",
        },
      },
    },
    lateCheckout: {
      title: "Late checkout",
      subtitle: "The agent manages late checkout requests: it validates the time, quotes the price and records the request.",
      enableLabel: "Manage late checkout",
      sections: {
        availability: {
          label: "Availability and hours",
        },
        extraPrice: {
          label: "Extra-hour price",
          hint: "Only the portion after the grace hours is charged.",
        },
        messages: {
          label: "Messages",
        },
      },
      fields: {
        standardCheckout: {
          label: "Standard checkout time",
        },
        courtesyHours: {
          label: "Grace hours (free)",
          unit: "h",
          hint: "Hours after standard checkout that are NEVER charged.",
        },
        latestTime: {
          label: "Latest checkout time",
          hint: "Absolute limit. Beyond the grace period, extra hours are charged.",
        },
        subjectToAvailability: {
          label: "Subject to availability",
          hint: "Extra hours are only offered if no other booking arrives that day.",
        },
        autoConfirm: {
          label: "Confirm automatically if the guest accepts",
          hint: "Only applies to free hours.",
        },
        pricingModel: {
          label: "Pricing model",
          options: {
            fixed: "Fixed price",
            perHour: "Per extra hour",
          },
        },
        price: {
          label: "Amount",
          hint: "Fixed price, or rate per extra hour beyond the grace period.",
        },
        freeIfAvailable: {
          label: "Free if there's no arrival the same day",
          hint: "If that day is free, the extra hours aren't charged either.",
        },
        paymentMethods: {
          label: "Payment methods for the extra hours",
          options: {
            online: "Online payment (Stripe) — the agent sends a link",
            cash: "Cash at checkout",
          },
          hint: "You can offer one or both. The agent will let the guest know.",
        },
        offerMessage: {
          label: "Offer message",
          placeholder: "Need more time? We can offer checkout until 13:00.",
        },
        confirmationMessage: {
          label: "Confirmation message",
          placeholder: "Done! Your late checkout is confirmed. Enjoy those extra hours 🙌",
        },
      },
    },
    invoicing: {
      title: "Invoice issuing",
      subtitle: "The agent sends the guest a link to complete their tax details and download their electronic invoice, using the billing issuer linked to this listing.",
      enableLabel: "Manage invoice requests",
      sections: {
        whenSend: {
          label: "When is the invoice link sent?",
          hint: "Choose how the link reaches the guest. The options for the mode you pick appear below.",
        },
        requirements: {
          label: "Requirements to send it instantly",
          hint: "The agent only delivers the link to guests who have already completed check-in and paid for the booking in full; once they fill in their details, the invoice is issued and downloaded instantly. For OTA bookings (Booking/Airbnb) the invoice can only be received once the service has started (the stay has begun) and payment is complete.",
        },
        manualReview: {
          label: "Manual review",
          hint: "Only the guest's request is recorded and stays pending in «Invoices awaiting sending» for you to issue manually. No invoice is generated automatically, and it doesn't matter when they ask or whether the booking is paid (you review it).",
        },
        fiscalData: {
          label: "Issuer's tax details",
          hint: "Tax details (legal name, tax ID, series, VAT, Verifactu) are NOT configured here: they're taken from the billing issuer linked to this listing in My account → Electronic invoicing.",
        },
        sendToGuest: {
          label: "Sending to the guest",
        },
      },
      modes: {
        after_checkout: {
          label: "Only on request",
          desc: "The AI concierge replies when the guest asks for their invoice.",
        },
        always_after_checkout: {
          label: "Always, when the stay ends",
          desc: "To everyone who has completed check-in, whether they ask or not. Recommended.",
        },
      },
      cards: {
        on_request: {
          label: "As soon as they ask",
          desc: "They get the link right away, if they meet the requirements.",
        },
        after_checkout: {
          label: "When their stay ends",
          desc: "The request is saved and the link reaches them after checkout.",
        },
        manual: {
          label: "Just record it",
          desc: "It stays pending and you issue it by hand.",
        },
      },
      fields: {
        autoIssueIfNotSubmitted: {
          label: "Issue the invoice even if they don't fill in the form",
          hint: "It's issued using their check-in details and the PDF is sent to them by email and WhatsApp. An issued invoice can't be edited.",
        },
        autoIssueAfterHours: {
          label: "How long to wait before issuing it",
          options: {
            h24: "24 hours after sending the link",
            h48: "48 hours after sending the link",
            h72: "72 hours after sending the link",
          },
        },
        manualSendLink: {
          label: "Send the guest the link to complete their tax details",
          hint: "If you enable it, the agent sends the link so the guest can provide their details in advance. The invoice is still issued manually.",
        },
        sendToGuest: {
          label: "Send the link to the guest via",
          options: {
            email: "Email",
            whatsapp: "WhatsApp",
          },
        },
      },
    },
    extendStay: {
      title: "Extend stay",
      subtitle: "The agent manages requests to add nights: it checks availability, quotes the price and records the request.",
      enableLabel: "Allow requests to extend the stay",
      sections: {
        availability: {
          label: "Availability",
          hint: "The maximum number of nights is set automatically by the next booking on the calendar.",
        },
        price: {
          label: "Price",
        },
        messages: {
          label: "Messages",
        },
      },
      fields: {
        autoConfirm: {
          label: "Extend the booking automatically on confirmation",
          hint: "If you turn this off, it stays a request for you to approve.",
        },
        useListingRate: {
          label: "Use the listing's nightly rate",
          hint: "Calculates the rate from the booking price.",
        },
        pricePerNight: {
          label: "Price per additional night",
          hint: "Only if you don't use the listing's rate.",
        },
        paymentMethods: {
          label: "Payment methods for the extra nights",
          options: {
            online: "Online payment (Stripe) — the agent sends a link",
            cash: "Cash",
          },
        },
        offerMessage: {
          label: "Offer message",
          placeholder: "Want to stay longer? I can add nights to your booking, subject to availability.",
        },
        confirmationMessage: {
          label: "Confirmation message",
          placeholder: "Great! I've recorded the extra nights. We'll confirm as soon as payment is complete 🙌",
        },
      },
    },
  },
  preview: {
    common: {
      noCost: "no charge",
      subjectToAvailability: " Subject to availability.",
    },
    default: "Hi! How can I help you? 😊",
    knowledge: {
      guestWifi: "What's the WiFi password? 📶",
      wifiAnswer: "Sure! The WiFi network is *{name}* and the password is *{pass}*. Enjoy! 😊",
      wifiNameFallback: "the property's",
      wifiPassFallback: "••••••",
      genericAnswer: "Of course! I'll send you right now all the information you need about the property 😊",
    },
    checkin: {
      propFallback: "tu alojamiento",
      welcome: "🇪🇸 *Hola* 👋 Soy 🤖 el asistente virtual de *{prop}*.\nEstoy aquí para ayudarte con el proceso de check-in.\n\nPresiona 'Ok' para continuar con el check-in.\n\n🇬🇧 *Hi* 👋 I'm 🤖 the virtual assistant of *{prop}*.\nI'll help you with the check-in process.\n\nPress 'Ok' to continue with the check-in.",
      ok: "Ok",
      chooseLang: "Elige tu idioma · Choose your language",
      langEs: "Español",
      langEn: "English",
      linkMsg: "*Gracias* 👋 Este es tu link para hacer el Check-In. 😎\n\n{link}",
    },
    newBookings: {
      offerDefault: "Of course! 😊 You can book directly here. I'll send you the link to pick your dates:",
      whoAsk: "Who am I speaking with?",
      whoAnswer: "Hi! I'm the assistant for {biz} 😊 How can I help you?",
      guestGreeting: "Hi! Do you have anything available this weekend? 😊",
      howBook: "Great, how do I book?",
      linkLine: "{offer}\n\n🔗 Book here: {link}",
    },
    earlyAccess: {
      guestAsk: "Could I check in earlier? I arrive around noon 🙏",
      tentativeReply: "We've noted your early arrival at 12:00. We'll do our best to have it ready, but it depends on the previous guest's checkout and cleaning. We'll let you know as soon as it's available 🙏",
      confirmedReply: "We've registered your early arrival at 12:00. We'll confirm shortly 🙏",
    },
    lateCheckout: {
      courtesyPart: "You have {hours}h of free grace time after {time}. ",
      costPart: "Checking out by {time} costs {price}{perHour}.",
      perHourSuffix: " per extra hour",
      payBoth: " You can pay online or in cash at checkout.",
      payCash: " Payment is in cash at checkout.",
      guestAsk: "Can I check out later tomorrow? 😅",
      guestConfirm: "OK, I confirm 👍",
      cashExtra: " (or in cash at checkout)",
      confirmPayDefault: "Done! To confirm, complete the payment{extra}:",
      payLabel: "Pay {price}",
      confirmCashDefault: "Done! Your late checkout is confirmed. The {price} payment is in cash at checkout. 😊",
    },
    invoicing: {
      guestAsk: "Can you send me the invoice? 🧾",
      linkMsg: "Hi! 👋\n\nHere's your link to generate and download your electronic invoice:\n{link}\n\nYou can come back to this link whenever you need to access it.",
      thanks: "Thanks for your stay! 🙌 If you need your invoice, here's the link 👇",
      autoIssueNote: "_({hours}h later, if they haven't completed their details)_\n\nWe've issued your invoice using your check-in details and sent it to you by email. 📩",
      manualLinkMsg: "Hi! 👋\n\nHere's the link to complete your tax details:\n{link}\n\nWe'll review your request and issue the invoice manually. 😊",
      manualRegistered: "Hi! 👋 I've registered your invoice request. We'll review it and issue it manually as soon as possible. Thanks! 😊",
    },
    extendStay: {
      guestAsk: "Can I stay one more night? 🌙",
      guestConfirm: "Yes, one more night 👍",
      rateListing: "at your booking's rate",
      ratePerNight: "for {price}/night",
      nightSingular: "night",
      nightPlural: "nights",
      offerDefault: "Sure! I can add up to {max} {nights} {rate}.{avail}",
      cashExtra: " (or in cash)",
      confirmPayDefault: "Perfect! To confirm the extra night, complete the payment{extra}:",
      payLabel: "Pay for extra night",
      confirmCashDefault: "Perfect! I've registered the extra night. Payment is in cash and the host will confirm the extension. 😊",
    },
    general: {
      guestAsk: "Hi! Who am I speaking with?",
      whoWithName: "I'm {name}, your",
      whoNoName: "I'm your",
      answer: "Hi! {who} reception assistant. How can I help you?{emo}",
    },
  },
  nationalities: {
    auto: "Automatic (based on guest)",
    latam: "Latin America (neutral)",
    countries: {
      ES: "Spain",
      MX: "Mexico",
      CO: "Colombia",
      AR: "Argentina",
      CL: "Chile",
      PE: "Peru",
      VE: "Venezuela",
      EC: "Ecuador",
      UY: "Uruguay",
      PY: "Paraguay",
      BO: "Bolivia",
      CR: "Costa Rica",
      PA: "Panama",
      GT: "Guatemala",
      DO: "Dominican Rep.",
      PR: "Puerto Rico",
      CU: "Cuba",
      FR: "France",
      PT: "Portugal",
      BR: "Brazil",
      US: "United States",
      IT: "Italy",
      BE: "Belgium",
      MA: "Morocco",
      NL: "Netherlands",
      CH: "Switzerland",
    },
    styles: {
      latamNeutral: "Neutral Latin American",
      esES: "Standard — informal (tú) · «vale», «genial»",
      esMX: "Standard — «con gusto», «estacionamiento»",
      esCO: "General — formal (usted) · «con gusto»",
      esCOpaisa: "Paisa (Medellín) — «pues», «hágale»",
      esCOcosta: "Coastal (Caribbean) — «¡ajá!», «quihubo»",
      esCOrolo: "Rolo (Bogotá) — formal (usted) · «a la orden»",
      esCOvalle: "Valluno (Cali) — voseo · «vea pues»",
      esCOsantander: "Santander — direct · «de una»",
      esAR: "Voseo — «dale», «buenísimo»",
      esCL: "Warm — «estacionamiento»",
      esPE: "Formal (usted) — neutral Andean",
      esVE: "Informal (tú) — «chévere»",
      esEC: "Formal (usted) — neutral Andean",
      esUY: "Voseo — «ta», «bo»",
      esPY: "Voseo — warm",
      esBO: "Formal (usted) — Andean",
      esCR: "Voseo — «pura vida»",
      esPA: "Informal (tú) — Caribbean",
      esGT: "Formal (usted) — warm",
      esDO: "Informal (tú) — Caribbean",
      esPR: "Informal (tú) — Caribbean",
      esCU: "Informal (tú) — Caribbean",
      frFR: "French (France)",
      ptPT: "Portuguese (Portugal)",
      ptBR: "Portuguese (Brazil)",
      enUS: "American English",
      itIT: "Italian (Italy)",
      frBE: "French (Belgium)",
      nlBE: "Dutch / Flemish (Belgium)",
      arMA: "Arabic (Morocco)",
      frMA: "French (Morocco)",
      nlNL: "Dutch",
      deCH: "Swiss German",
      frCH: "Swiss French",
      itCH: "Swiss Italian",
    },
  },
};
